import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { useSupportMessages } from './useSupportMessages';
import { useImageUpload } from './useImageUpload';
import { cn } from '../../lib/utils';

/* v9 spec 6.4 — Message Us, functionally a straight port of the OLD
   SupportChat layout:

   - Rendered as a full-viewport portal (`createPortal` into document.body
     with `fixed inset-0`). This escapes any parent layout constraints so
     the header stays pinned to the top, the composer stays pinned to the
     bottom, and the messages list is the only thing that scrolls.
   - Desktop: the fixed portal is width-capped to match the phone column
     used by the rest of the app, centred on a slate backdrop.
   - Composer uses a <textarea> (not <input>) so the field can wrap and
     auto-grow. Font size is 16px — anything smaller triggers iPhone's
     auto-zoom when the input is focused, which is both jarring and hard
     to unzoom from.

   Underlying data flow is unchanged: live subscription via
   useSupportMessages, image upload via useImageUpload, optimistic state
   with `pending: true`, and sender-side tick rendering based on `readAt`.
*/
export function MessageUsView({ previewMessages }) {
  const navigate = useNavigate();
  const {
    messages: liveMessages,
    loading,
    error,
    sending,
    sendMessage,
    markAsRead,
    clearError,
    accountUUID,
  } = useSupportMessages();

  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    uploadImages,
    hasImages,
  } = useImageUpload();

  const messages = previewMessages?.length ? previewMessages : liveMessages;

  const [draft, setDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [imageErrors, setImageErrors] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const busy = sending || uploading;

  // Keep the most recent message in view.
  useEffect(() => {
    bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, hasImages, pendingImages.length]);

  // Mark as read whenever the message list grows.
  useEffect(() => {
    if (!previewMessages && messages.length > 0) {
      markAsRead();
    }
  }, [messages.length, markAsRead, previewMessages]);

  // Body scroll-lock while the portal is mounted so the background doesn't
  // wobble on iOS. Pair with the fixed-inset positioning below.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/app/help');
  };

  /* Auto-grow the textarea up to 120px, matching OLD SupportChat. */
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleSend = useCallback(async () => {
    const trimmed = draft.trim();
    if ((!trimmed && !hasImages) || busy || !accountUUID) return;

    const messageToSend = trimmed;
    setDraft('');
    clearError();
    // Reset textarea height so it snaps back to one line.
    requestAnimationFrame(() => autoGrow(textareaRef.current));

    if (hasImages) {
      setUploading(true);
      try {
        const preGeneratedId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const uploadedImages = await uploadImages(accountUUID, preGeneratedId);
        clearImages();
        setUploading(false);
        await sendMessage(messageToSend, uploadedImages, preGeneratedId);
      } catch (err) {
        console.error('MessageUsView upload error', err);
        setUploading(false);
        // Keep the images on failure so the user can retry.
        return;
      }
    } else {
      await sendMessage(messageToSend);
    }

    textareaRef.current?.focus();
  }, [draft, hasImages, busy, accountUUID, sendMessage, uploadImages, clearImages, clearError]);

  const handleFilePick = () => fileInputRef.current?.click();

  const handleFileInput = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const errs = await addImages(files);
    setImageErrors(errs);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const errs = await addImages(files);
      setImageErrors(errs);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      const errs = await addImages(files);
      setImageErrors(errs);
    }
  };

  const grouped = groupMessagesByDate(messages);

  const panel = (
    <div
      className="fixed inset-0 bg-slate-50 flex justify-center z-[9000]"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Inner column: width-capped on desktop, full-width on mobile. Flex
          column so header pins top / messages flex / composer pins bottom.
          Height comes from the parent's default `items-stretch`; we don't
          set `h-full` because combined with `md:my-6` it would overflow
          (100% + 48px) and push the composer below the viewport. */}
      <div className="w-full max-w-[420px] md:max-w-[520px] bg-white flex flex-col min-h-0 md:my-6 md:rounded-[28px] md:shadow-[0_25px_60px_-30px_rgba(15,23,42,0.35)] md:ring-1 md:ring-slate-200/70 md:overflow-hidden">
        {dragOver && (
          <div className="absolute inset-0 z-20 bg-blue-50/80 border-2 border-dashed border-blue-400 rounded-xl flex items-center justify-center text-[14px] text-blue-800 font-semibold pointer-events-none">
            Drop images to attach
          </div>
        )}

        {/* Header — pinned */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100 px-2 pt-3 pb-2 flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={handleBack}
            className="h-9 w-9 rounded-full inline-flex items-center justify-center text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <div className="flex-1 min-w-0 px-1">
            <h1 className="text-[16px] font-semibold text-slate-900 truncate">
              Message Us
            </h1>
            <p className="text-[11px] text-slate-500 truncate">
              Any feedback or issues? We try to get back as soon as we can. 
            </p>
          </div>
        </div>

        {/* Messages — scroll area */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {loading && !previewMessages ? (
            <p className="text-[12px] text-slate-400 text-center py-8">
              Loading&hellip;
            </p>
          ) : messages.length === 0 ? (
            <EmptyState />
          ) : (
            grouped.map((item, idx) =>
              item.type === 'date' ? (
                <DayDivider key={`d-${idx}`} date={item.date} />
              ) : item.message.senderType === 'user' ? (
                <UserMessage
                  key={item.message.id}
                  message={item.message}
                  onImageClick={(images, index) => setLightbox({ images, index })}
                />
              ) : (
                <AdminMessage
                  key={item.message.id}
                  message={item.message}
                  onImageClick={(images, index) => setLightbox({ images, index })}
                />
              ),
            )
          )}
          <div ref={bottomAnchorRef} />
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex-shrink-0 mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-2">
            <span className="text-[12px] text-red-700">{error}</span>
            <button
              type="button"
              onClick={clearError}
              className="text-red-500 hover:text-red-700"
              aria-label="Dismiss error"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {imageErrors.length > 0 && (
          <div className="flex-shrink-0 mx-4 mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
            {imageErrors.map((e, i) => (
              <div key={i} className="text-[11px] text-amber-800">{e}</div>
            ))}
          </div>
        )}

        {/* Pending-image strip — above composer */}
        {hasImages && (
          <div className="flex-shrink-0 bg-white border-t border-slate-100 px-3 pt-2 pb-1">
            <div className="flex gap-2 overflow-x-auto">
              {pendingImages.map((img) => (
                <PendingImageChip
                  key={img.id}
                  img={img}
                  onRemove={() => removeImage(img.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Composer — pinned bottom. Textarea with 16px font to avoid
            iPhone's focus-zoom. */}
        <div className="flex-shrink-0 bg-white border-t border-slate-100 px-3 py-2">
          <div className="flex items-end gap-2">
            <button
              type="button"
              aria-label="Attach image"
              onClick={handleFilePick}
              disabled={busy}
              className="h-10 w-10 rounded-full flex-shrink-0 inline-flex items-center justify-center text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              <Paperclip className="w-5 h-5" strokeWidth={2} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
            <div className="flex-1 min-w-0">
              <textarea
                ref={textareaRef}
                rows={1}
                value={draft}
                onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type a message"
                disabled={busy}
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50"
                /* 16px font-size is what prevents iPhone Safari from
                   auto-zooming the page when the input is focused. Don't
                   reduce it below 16px. */
                style={{ fontSize: '16px', lineHeight: '1.3', minHeight: '40px', maxHeight: '120px' }}
              />
            </div>
            <button
              type="button"
              aria-label="Send"
              onClick={handleSend}
              disabled={busy || (!draft.trim() && !hasImages)}
              className="h-10 w-10 rounded-full flex-shrink-0 inline-flex items-center justify-center text-white disabled:opacity-50"
              style={{ background: '#0f172a' }}
            >
              {busy ? (
                <span className="w-4 h-4 rounded-full border-2 border-white/60 border-t-white animate-spin" />
              ) : (
                <Send className="w-4 h-4" strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );

  return createPortal(panel, document.body);
}

export default MessageUsView;

/* ──────────────── helpers ──────────────── */

const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;

function renderContentWithLinks(content, isOwn) {
  if (!content) return null;
  const parts = content.split(URL_REGEX);
  return parts.map((part, i) =>
    part.match(/^https?:\/\//) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'underline break-all',
          isOwn ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800',
        )}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function formatTime(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function groupMessagesByDate(messages) {
  const groups = [];
  let currentKey = null;
  for (const m of messages) {
    const date = m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt);
    const key = date.toDateString();
    if (key !== currentKey) {
      currentKey = key;
      groups.push({ type: 'date', date });
    }
    groups.push({ type: 'message', message: m });
  }
  return groups;
}

function formatDayLabel(date) {
  const d = date instanceof Date ? date : new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

/* ──────────────── row components ──────────────── */

function DayDivider({ date }) {
  return (
    <div className="flex items-center justify-center my-3">
      <span className="text-[11px] text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
        {formatDayLabel(date)}
      </span>
    </div>
  );
}

function UserMessage({ message, onImageClick }) {
  const isRead = message.readAt != null;
  return (
    <>
      <div className="flex justify-end mb-1">
        <div
          className={cn(
            'max-w-[80%] rounded-2xl px-3.5 py-2 text-[14px] text-white leading-relaxed whitespace-pre-wrap break-words',
            message.pending && 'opacity-70',
          )}
          style={{ background: '#0f172a', borderBottomRightRadius: 4 }}
        >
          {renderContentWithLinks(message.content, true)}
          {message.images?.length > 0 && (
            <ImageGrid
              images={message.images}
              onImageClick={(idx) => onImageClick(message.images, idx)}
            />
          )}
        </div>
      </div>
      <div className="text-[10px] text-slate-400 text-right mb-3 mr-1 inline-flex items-center gap-1 w-full justify-end">
        <span>{formatTime(message.createdAt)}</span>
        {isRead ? (
          <CheckCheck className="w-3.5 h-3.5 text-blue-400" strokeWidth={2.5} />
        ) : (
          <Check className="w-3 h-3 text-slate-400" strokeWidth={2.5} />
        )}
      </div>
    </>
  );
}

function AdminMessage({ message, onImageClick }) {
  return (
    <>
      <div className="flex justify-start mb-1">
        <div
          className="max-w-[80%] rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed whitespace-pre-wrap break-words"
          style={{ background: '#f1f5f9', color: '#0f172a', borderBottomLeftRadius: 4 }}
        >
          {renderContentWithLinks(message.content, false)}
          {message.images?.length > 0 && (
            <ImageGrid
              images={message.images}
              onImageClick={(idx) => onImageClick(message.images, idx)}
            />
          )}
        </div>
      </div>
      <div className="text-[10px] text-slate-400 ml-1 mb-3">
        {formatTime(message.createdAt)}
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <p className="text-[14px] text-slate-600 font-semibold mb-1">
        We love hearing from you!
      </p>
      <p className="text-[12px] text-slate-500 leading-relaxed max-w-[300px] mx-auto">
        Feedback, questions, or ideas &mdash; tell us everything and we&rsquo;ll get
        back ASAP.
      </p>
    </div>
  );
}

/* ──────────────── images ──────────────── */

function ImageGrid({ images, onImageClick }) {
  if (!images || images.length === 0) return null;
  if (images.length === 1) {
    return (
      <div className="mt-1 mb-1">
        <img
          src={images[0].url}
          alt={images[0].name || 'Image'}
          className="rounded-lg max-w-full max-h-64 cursor-pointer object-cover"
          onClick={() => onImageClick(0)}
          loading="lazy"
        />
      </div>
    );
  }
  if (images.length === 2) {
    return (
      <div className="mt-1 mb-1 grid grid-cols-2 gap-1">
        {images.map((img, i) => (
          <img
            key={i}
            src={img.url}
            alt={img.name || 'Image'}
            className="rounded-lg w-full h-32 object-cover cursor-pointer"
            onClick={() => onImageClick(i)}
            loading="lazy"
          />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-1 mb-1 grid grid-cols-2 gap-1">
      {images.map((img, i) => (
        <img
          key={i}
          src={img.url}
          alt={img.name || 'Image'}
          className={cn(
            'rounded-lg w-full h-32 object-cover cursor-pointer',
            images.length % 2 === 1 && i === images.length - 1 && 'col-span-2 h-40',
          )}
          onClick={() => onImageClick(i)}
          loading="lazy"
        />
      ))}
    </div>
  );
}

function PendingImageChip({ img, onRemove }) {
  return (
    <div className="relative flex-shrink-0">
      <img
        src={img.previewUrl}
        alt={img.file?.name || 'Pending'}
        className={cn(
          'w-16 h-16 rounded-lg object-cover border border-slate-200',
          img.status === 'uploading' && 'opacity-60',
          img.status === 'error' && 'ring-2 ring-red-300',
        )}
      />
      {img.status === 'uploading' && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white bg-black/40 rounded-lg">
          {img.progress}%
        </div>
      )}
      {img.status !== 'uploading' && (
        <button
          type="button"
          aria-label="Remove image"
          onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
        >
          <X className="w-3 h-3" strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

function ImageLightbox({ images, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrentIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentIndex((i) => Math.min(images.length - 1, i + 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}
      {currentIndex > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setCurrentIndex((i) => i - 1); }}
          aria-label="Previous"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}
      <img
        src={images[currentIndex].url}
        alt={images[currentIndex].name || 'Image'}
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {currentIndex < images.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
          onClick={(e) => { e.stopPropagation(); setCurrentIndex((i) => i + 1); }}
          aria-label="Next"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
    </div>,
    document.body,
  );
}
